import { useCallback, useEffect, useRef } from 'react';
import { IUser } from '../types/damTypes';
import {
  AUTH_ACTIVITY_KEY,
  AUTH_EVENT_KEY,
  addAuthEventListener,
  getHeartbeatIntervalMs,
  getSessionTiming,
  heartbeat,
  logout,
  recordUserActivity,
} from '../services/authService';

type SessionEndReason = 'idle_timeout' | 'password_changed' | 'session_invalid' | string;

export function useAuthSessionLifecycle(
  currentUser: IUser | null,
  onSessionEnded: (reason?: SessionEndReason) => void
) {
  const timerRef = useRef<number | null>(null);
  const heartbeatInFlightRef = useRef(false);
  const lastHeartbeatAtRef = useRef(0);
  const finishingRef = useRef(false);
  const onSessionEndedRef = useRef(onSessionEnded);

  useEffect(() => {
    onSessionEndedRef.current = onSessionEnded;
  }, [onSessionEnded]);

  const clearExpiryTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const finishSession = useCallback(async (reason: SessionEndReason, options?: { server?: boolean }) => {
    if (finishingRef.current) return;
    finishingRef.current = true;
    try {
      await logout({ server: options?.server !== false, reason });
    } finally {
      onSessionEndedRef.current(reason);
    }
  }, []);

  const scheduleExpiryTimer = useCallback(() => {
    clearExpiryTimer();
    if (!currentUser) return;

    const timing = getSessionTiming();
    if (!timing.session || !timing.idleExpiresAt) return;

    if (timing.expiredReason) {
      void finishSession(timing.expiredReason, { server: true });
      return;
    }

    const delay = Math.max(1000, Math.min(timing.idleExpiresAt - Date.now(), 60 * 1000));
    timerRef.current = window.setTimeout(() => {
      scheduleExpiryTimer();
    }, delay);
  }, [clearExpiryTimer, currentUser, finishSession]);

  const sendHeartbeatIfNeeded = useCallback(async () => {
    if (!currentUser || heartbeatInFlightRef.current) return;

    const timing = getSessionTiming();
    if (!timing.session) return;

    const now = Date.now();
    if (timing.expiredReason) {
      await finishSession(timing.expiredReason, { server: true });
      return;
    }

    if (lastHeartbeatAtRef.current > 0 && now - lastHeartbeatAtRef.current < getHeartbeatIntervalMs()) {
      return;
    }

    heartbeatInFlightRef.current = true;
    try {
      const response = await heartbeat();
      if (response.data?.session) {
        lastHeartbeatAtRef.current = now;
      } else if (response.code === 'AUTH_SESSION_IDLE_TIMEOUT' || response.status === 401) {
        await finishSession(response.code === 'AUTH_SESSION_IDLE_TIMEOUT' ? 'idle_timeout' : 'session_invalid', { server: false });
      }
    } finally {
      heartbeatInFlightRef.current = false;
      scheduleExpiryTimer();
    }
  }, [currentUser, finishSession, scheduleExpiryTimer]);

  useEffect(() => {
    if (!currentUser) {
      clearExpiryTimer();
      finishingRef.current = false;
      return;
    }

    const handlePotentialActivity = () => {
      const timing = getSessionTiming();
      if (!timing.session) return;

      if (timing.expiredReason) {
        void finishSession(timing.expiredReason, { server: true });
        return;
      }

      recordUserActivity(Date.now());
      scheduleExpiryTimer();
      void sendHeartbeatIfNeeded();
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === AUTH_ACTIVITY_KEY) {
        scheduleExpiryTimer();
        return;
      }

      if (event.key === AUTH_EVENT_KEY && event.newValue) {
        try {
          const payload = JSON.parse(event.newValue) as { type?: string; reason?: SessionEndReason };
          if (payload.type === 'logout') {
            onSessionEndedRef.current(payload.reason);
            return;
          }
        } catch {}
        scheduleExpiryTimer();
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        handlePotentialActivity();
      }
    };

    const unsubscribe = addAuthEventListener((payload) => {
      if (payload.type === 'logout') {
        onSessionEndedRef.current(payload.reason);
        return;
      }
      scheduleExpiryTimer();
    });

    recordUserActivity(Date.now(), true);
    lastHeartbeatAtRef.current = Date.now();
    scheduleExpiryTimer();

    window.addEventListener('mousemove', handlePotentialActivity, { passive: true });
    window.addEventListener('mousedown', handlePotentialActivity, { passive: true });
    window.addEventListener('keydown', handlePotentialActivity);
    window.addEventListener('touchstart', handlePotentialActivity, { passive: true });
    window.addEventListener('focus', handlePotentialActivity);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorage);

    return () => {
      unsubscribe();
      clearExpiryTimer();
      window.removeEventListener('mousemove', handlePotentialActivity);
      window.removeEventListener('mousedown', handlePotentialActivity);
      window.removeEventListener('keydown', handlePotentialActivity);
      window.removeEventListener('touchstart', handlePotentialActivity);
      window.removeEventListener('focus', handlePotentialActivity);
      window.removeEventListener('storage', handleStorage);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [clearExpiryTimer, currentUser, finishSession, scheduleExpiryTimer, sendHeartbeatIfNeeded]);
}
